import React, { useEffect, useState } from 'react';
import { useFirebase } from 'react-redux-firebase';
import { useInternalApiClient } from 'hooks/useInternalApiClient';
import { useSelector, useDispatch } from 'react-redux';
import * as Yup from 'yup';
import { keys } from 'lodash';
// library components
import { Formik, Form } from 'formik';
import { Button, Typography, Box, makeStyles, Divider } from '@material-ui/core';
import CheckCircleOutlineOutlinedIcon from '@material-ui/icons/CheckCircleOutlineOutlined';
// cr components
// actions
import { enqueueErrorSnackbar, enqueueSuccessSnackbar } from 'redux/actions/notifierActions';
import { showAppOverlaySpinner } from 'redux/actions/facilitatorSessionActions';
// selectors
import { facilitatorSubscriptionSelector } from 'redux/selectors/facilitatorSessionSelectors';
import Spinner from 'components/Spinner';
// utils
import { changeEmail } from '../../utils/changeEmail';

const useStyles = makeStyles(theme => ({
  label: {
    marginRight: 10,
    color: 'rgba(0, 0, 0, 0.54)',
    display: 'flex',
    alignItems: 'center',
  },
  premuimSubscription: {
    color: '#CEB070',
  },
  button: {
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.primary.main,
    padding: 0,
    textTransform: 'none',
    fontSize: 16,
    fontWeight: 400,
    '&:hover': {
      background: 'unset',
    },
  },
  checkIcon: {
    color: theme.palette.success.main,
    fontSize: 16,
    marginLeft: 4,
  },
  success: {
    color: theme.palette.success.main,
  },
}));

/**
 * Form used by particicpants to change their display name
 */
const FacilitatorAccountSEttingsForm = ({ closeDialog }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const authUser = useSelector(state => state.firebase.auth);
  const apiClient = useInternalApiClient();
  const profile = useSelector(state => state.firebase.profile);
  const isMuralAuthorized = profile.isMuralAuthorized ?? false;
  const { isInternalUser } = useSelector(facilitatorSubscriptionSelector());

  const showMuralAuthButton =
    profile.email === 'douglas@voltagecontrol.com' || process.env.REACT_APP_HOSTING_ENV === 'local';

  const firebase = useFirebase();
  const [showAuthMuralButton, setShowAuthMuralButton] = useState(!isMuralAuthorized);

  const formSchema = Yup.object().shape({
    displayName: Yup.string().required('Required'),
    email: Yup.string().email().required(),
  });

  useEffect(() => {
    (async () => {
      if (!isMuralAuthorized) {
        try {
          const result = await apiClient.users.syncMuralAuth();

          if (result.isMuralAuthorized) {
            setShowAuthMuralButton(false);
          } else {
            setShowAuthMuralButton(true);
          }
        } catch (err) {
          console.error('Error syncing Mural auth status', err);
          setShowAuthMuralButton(true);
        }
      }
    })();
  }, [apiClient, isMuralAuthorized]);

  const getMuralAuthUrl = async () => {
    dispatch(showAppOverlaySpinner(true));

    try {
      const authUrl = await apiClient.users.getMuralAuthUrl(window.location.href);
      window.location.replace(authUrl);
    } catch (err) {
      dispatch(showAppOverlaySpinner(false));
      dispatch(enqueueErrorSnackbar('Error getting auth url'));
      console.error('get auth url error', err);
    }
  };

  const disconnectMuralAccount = async () => {
    try {
      await apiClient.users.disconnectMural();
      dispatch(enqueueSuccessSnackbar('Mural Account Disconnected'));
    } catch (error) {
      dispatch(enqueueErrorSnackbar('Error disconnecting Mural account'));
      console.error(error);
    }
  };

  return (
    <Formik
      initialValues={{
        displayName: authUser.displayName,
        email: authUser.email,
      }}
      validationSchema={formSchema}
      onSubmit={async (values, { setSubmitting }) => {
        const currentUser = firebase.auth().currentUser;
        const currentEmail = currentUser.email;
        const update = {};

        if (values.displayName !== currentUser.displayName) {
          update.displayName = values.displayName;
        }

        if (keys(update).length) {
          try {
            await apiClient.users.updateProfile(update);
            await firebase.updateAuth(update, true);
          } catch (err) {
            setSubmitting(false);
            dispatch(enqueueErrorSnackbar('Error updating name'));
            console.error('Error updating settings: ', err);
            return;
          }
        }

        if (values.email !== currentEmail) {
          try {
            await changeEmail(currentUser.uid, values.email);
            // user will have to log back in on reload for new address to take effect
            window.location.reload();
            return;
          } catch (err) {
            dispatch(enqueueErrorSnackbar(err.message));
          }
        }

        setSubmitting(false);
        closeDialog();
      }}
    >
      {({ isSubmitting, submitForm, resetForm }) => (
        <Form style={{ paddingBottom: 20 }}>
          <Typography>Email: {authUser.email}</Typography>
          {isInternalUser && (
            <Box>
              <Box className="my-3">
                <Typography className={classes.label}>User ID: {authUser.uid}</Typography>
              </Box>
            </Box>
          )}
          <Divider />
          {showMuralAuthButton && (
            <>
              {!showAuthMuralButton && !isMuralAuthorized && (
                <Box className="my-3">
                  <Spinner />
                </Box>
              )}
              {showAuthMuralButton && (
                <Box className="my-3">
                  <Typography className={classes.label}>Mural Account: Not Connected</Typography>
                  <Box display="flex" mt={1}>
                    <Button
                      color="primary"
                      elevation={0}
                      variant="contained"
                      onClick={getMuralAuthUrl}
                      type="button"
                      size="small"
                      className="mr-1"
                    >
                      Authorize Mural Account
                    </Button>
                  </Box>
                </Box>
              )}
              {isMuralAuthorized && (
                <Box className="my-3">
                  <Typography className={classes.label}>
                    Mural Account: &nbsp;<span className={classes.success}>Authorized</span>
                    <CheckCircleOutlineOutlinedIcon className={classes.checkIcon} />
                  </Typography>
                  <Box display="flex" mt={1}>
                    <Button
                      color="primary"
                      elevation={0}
                      variant="contained"
                      onClick={disconnectMuralAccount}
                      type="button"
                      size="small"
                      className="mr-1"
                    >
                      Disconnect Mural Account
                    </Button>
                  </Box>
                </Box>
              )}
            </>
          )}
        </Form>
      )}
    </Formik>
  );
};

export default FacilitatorAccountSEttingsForm;
